import { useState, useEffect } from 'react'
import { fetchWithAuth } from '../../utils/api'

interface UserLog {
  id: number
  username: string
  email: string
  action: string
  resource_type: string
  resource_id: number | null
  details: string | null
  ip_address: string | null
  user_agent: string | null
  timestamp: string
}

interface LogsResponse {
  logs: UserLog[]
  pagination: {
    page: number
    limit: number
    total: number
    pages: number
  }
}

function LogsManager() {
  const [logs, setLogs] = useState<UserLog[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 50,
    total: 0,
    pages: 0
  })

  useEffect(() => {
    fetchLogs()
  }, [pagination.page])

  const fetchLogs = async () => {
    try {
      setLoading(true)
      const response = await fetchWithAuth(`/api/admin/logs?page=${pagination.page}&limit=${pagination.limit}`)
      if (!response.ok) {
        throw new Error('Failed to fetch logs')
      }
      const data: LogsResponse = await response.json()
      setLogs(data.logs)
      setPagination(data.pagination)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load logs')
    } finally {
      setLoading(false)
    }
  }

  const formatTimestamp = (timestamp: string) => {
    return new Date(timestamp).toISOString().slice(0, 19).replace('T', ' ')
  }

  const formatDetails = (details: string | null) => {
    if (!details) return '-'
    try {
      const parsed = JSON.parse(details)
      return JSON.stringify(parsed, null, 2)
    } catch {
      return details
    }
  }

  const getActionColor = (action: string) => {
    if (action.includes('delete')) return '#e74c3c'
    if (action.includes('post')) return '#27ae60'
    if (action.includes('put')) return '#f39c12'
    return '#3498db'
  }

  const getResourceIcon = (resourceType: string) => {
    switch (resourceType) {
      case 'site': return '🏢'
      case 'area': return '🗺️'
      case 'map_link': return '🔗'
      default: return '📝'
    }
  }

  if (loading && logs.length === 0) {
    return <div className="loading">Loading user logs...</div>
  }

  return (
    <div>
      <h2>User Activity Logs</h2>
      
      {error && <div className="error">{error}</div>}

      <div style={{ marginBottom: '1rem', color: '#666', fontSize: '0.9rem' }}>
        Showing {logs.length} of {pagination.total} log entries
        {pagination.pages > 1 && ` (Page ${pagination.page} of ${pagination.pages})`}
      </div>

      <div className="table-container">
        {logs.length === 0 ? (
          <div className="loading">No log entries found</div>
        ) : (
          <table className="table">
            <thead>
              <tr>
                <th>Time</th>
                <th>User</th>
                <th>Action</th>
                <th>Resource</th>
                <th>Details</th>
                <th>IP Address</th>
              </tr>
            </thead>
            <tbody>
              {logs.map((log) => (
                <tr key={log.id}>
                  <td className="date-cell" style={{ minWidth: '120px' }}>
                    {formatTimestamp(log.timestamp)}
                  </td>
                  <td>
                    <div>
                      <strong>{log.username}</strong>
                    </div>
                    <div style={{ fontSize: '0.8rem', color: '#666' }}>
                      {log.email}
                    </div>
                  </td>
                  <td>
                    <span 
                      style={{ 
                        color: getActionColor(log.action),
                        fontWeight: '500',
                        textTransform: 'uppercase',
                        fontSize: '0.8rem'
                      }}
                    >
                      {log.action}
                    </span>
                  </td>
                  <td>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                      <span>{getResourceIcon(log.resource_type)}</span>
                      <div>
                        <div style={{ fontWeight: '500' }}>
                          {log.resource_type.replace('_', ' ')}
                        </div>
                        {log.resource_id && (
                          <div style={{ fontSize: '0.8rem', color: '#666' }}>
                            ID: {log.resource_id}
                          </div>
                        )}
                      </div>
                    </div>
                  </td>
                  <td style={{ maxWidth: '300px' }}>
                    {log.details ? (
                      <details style={{ fontSize: '0.8rem' }}>
                        <summary style={{ cursor: 'pointer', color: '#3498db' }}>
                          View Details
                        </summary>
                        <pre style={{ 
                          marginTop: '0.5rem', 
                          padding: '0.5rem', 
                          background: '#f8f9fa',
                          borderRadius: '4px',
                          fontSize: '0.75rem',
                          overflow: 'auto',
                          maxHeight: '200px'
                        }}>
                          {formatDetails(log.details)}
                        </pre>
                      </details>
                    ) : (
                      <span style={{ color: '#999' }}>-</span>
                    )}
                  </td>
                  <td style={{ fontSize: '0.8rem', fontFamily: 'monospace' }}>
                    {log.ip_address || '-'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {pagination.pages > 1 && (
        <div style={{ 
          marginTop: '1rem', 
          display: 'flex', 
          justifyContent: 'center', 
          gap: '0.5rem',
          alignItems: 'center'
        }}>
          <button 
            className="btn" 
            disabled={pagination.page <= 1}
            onClick={() => setPagination(prev => ({ ...prev, page: prev.page - 1 }))}
          >
            ← Previous
          </button>
          <span style={{ fontSize: '0.9rem', color: '#666' }}>
            Page {pagination.page} of {pagination.pages}
          </span>
          <button 
            className="btn" 
            disabled={pagination.page >= pagination.pages}
            onClick={() => setPagination(prev => ({ ...prev, page: prev.page + 1 }))}
          >
            Next →
          </button>
        </div>
      )}

      <div style={{ 
        marginTop: '2rem', 
        padding: '1rem', 
        backgroundColor: '#f8f9fa', 
        borderRadius: '8px',
        fontSize: '0.9rem'
      }}>
        <h4 style={{ margin: '0 0 0.5rem 0', color: '#2c3e50' }}>📊 Activity Tracking</h4>
        <ul style={{ margin: 0, paddingLeft: '1.5rem', color: '#666' }}>
          <li>All admin actions are automatically logged</li>
          <li>Logs include user information, timestamps, and action details</li>
          <li>Use this view to monitor system usage and audit changes</li>
          <li>Logs are ordered by most recent activity first</li>
        </ul>
      </div>
    </div>
  )
}

export default LogsManager